import { useQueryClient, type QueryClient } from "@tanstack/react-query"
import { useGetConfig } from "@/api/generated/config/config"
import {
  getGetMeQueryKey,
  getGetMeQueryOptions,
  useAcceptInvite as useAcceptInviteMutation,
  useBeginTotpEnrolment as useBeginTotpEnrolmentMutation,
  useConfirmTotpEnrolment as useConfirmTotpEnrolmentMutation,
  useForgotPassword as useForgotPasswordMutation,
  useLogin as useLoginMutation,
  useLoginWithGoogle as useLoginWithGoogleMutation,
  useRegister as useRegisterMutation,
  useResendVerificationEmail as useResendVerificationEmailMutation,
  useResetPassword as useResetPasswordMutation,
  useStepUp as useStepUpMutation,
  useUnsubscribeEmail,
  useVerifyEmail as useVerifyEmailMutation,
  useVerifyTotp as useVerifyTotpMutation,
} from "@/api/generated/identity/identity"
import type { Me } from "@/api/generated/model"
import { STALE } from "@/api/query-client"
import { STEP_UP_HEADERS } from "@/api/step-up-header"
import { stepUpStore } from "@/auth/step-up-store"

/**
 * The auth slice's only door to the generated client (LLD §1.1, §9.1). Each
 * wrapper adds what must happen after its call; screens never import the
 * generated hooks.
 */

/** `getConfig`, the one config rule (LLD §5.7): `reference`, refetched on focus. */
export function useAuthConfig() {
  return useGetConfig({ query: { staleTime: STALE.reference } })
}

export const useLogin = () => useLoginMutation()
export const useLoginWithGoogle = () => useLoginWithGoogleMutation()
export const useVerifyTotp = () => useVerifyTotpMutation()
export const useRegister = () => useRegisterMutation()
export const useForgotPassword = () => useForgotPasswordMutation()
export const useResetPassword = () => useResetPasswordMutation()
export const useAcceptInvite = () => useAcceptInviteMutation()
export const useResendVerificationEmail = () => useResendVerificationEmailMutation()

/** A verified address changes `me.user.emailVerified`: refetch it when signed in. */
/** The footer link of an optional email (DV-156): no session, the token is the proof. */
export const useUnsubscribe = () => useUnsubscribeEmail()

export function useVerifyEmail() {
  const queryClient = useQueryClient()
  return useVerifyEmailMutation({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() })
      },
    },
  })
}

/** Proves the password again; the token goes to the memory step-up store (LLD §6.7). */
export function useStepUp() {
  return useStepUpMutation({
    mutation: {
      onSuccess: (s) => {
        stepUpStore.set({ token: s.stepUpToken, expiresAt: Date.parse(s.expiresAt) })
      },
    },
  })
}

/**
 * `x-step-up: true`: `X-Step-Up` is read from the store when the request is
 * sent. The answer carries the TOTP secret: `gcTime: 0` drops it from the
 * mutation cache as soon as the screen lets go of it.
 */
export function useBeginTotpEnrolment() {
  return useBeginTotpEnrolmentMutation({
    request: { headers: STEP_UP_HEADERS },
    mutation: { gcTime: 0 },
  })
}

/**
 * The recovery codes live in this mutation's result only: never in the query
 * cache, never in storage (PDD §12.6). `gcTime: 0` drops even the mutation
 * cache entry as soon as the screen unmounts.
 *
 * The cached `me` is marked enrolled at once: nothing observes it on the
 * enrolment screen, so an invalidation alone leaves the stale "TOTP off" for
 * the route guard to read on the way out, and it sends the user straight back.
 */
export function useConfirmTotpEnrolment() {
  const queryClient = useQueryClient()
  return useConfirmTotpEnrolmentMutation({
    request: { headers: STEP_UP_HEADERS },
    mutation: {
      gcTime: 0,
      onSuccess: () => {
        queryClient.setQueryData<Me>(getGetMeQueryKey(), (me) =>
          me ? { ...me, user: { ...me.user, totpEnabled: true } } : me,
        )
        void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() })
      },
    },
  })
}

/**
 * The caller, read right after a sign-in to decide where to go (staff without
 * TOTP enrol first). It fills the same cache entry the session's `useMe`
 * reads, so the shell does not ask again.
 */
export function fetchMe(queryClient: QueryClient): Promise<Me> {
  return queryClient.query(getGetMeQueryOptions({ query: { staleTime: 0 } }))
}
